import { Dispatch } from '@reduxjs/toolkit';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  actionGiveConsent,
  actionLoadForm,
  actionRemoveConsent,
} from '../../store/actions/form.actions';
import { actionLoadResponse } from '../../store/actions/responses.actions';
import { actionUIOpen } from '../../store/actions/ui.actions';
import {
  ParentAppMessage,
  ParentAppMessageCommand,
  WidgetAppMessageCommand,
  WidgetAppMessage,
} from './types/index.d';
import {
  IncomingLoadFormMessage,
  IncomingOpenMessage,
  IncomingResponseMessage,
  SendCloseMessageData,
  SendExitedMessageData,
  SendInitMessageData,
  SendMessagePayload,
  SendRequestMessageData,
} from './types/messages';
import uuidv4 from 'uuid-browser';
import APIService from '../api/index.api';
import { APIBaseService } from '../api/base.api';
import { StatsType } from '../api/types/stats.response.d';
import { checkForTapformDemoSubmissionParam } from '../../helpers';
import { checkIfValidTapformMessageKey, createMessageKey } from './constants';

class ParentAppService {
  private static readonly staticHostingDomains = [
    'localhost:3004',
    'client.tapform.io',
    '192.168.1.177:3004',
    'forms.tapform.io',
    'client.tapform.com',
    'forms.tapform.com',
  ];

  private static readonly dashboardDomains = [
    'https://dashboard.tapform.io',
    'https://dashboard.tapform.io',
    'https://dashboard.tapform.com',
    'http://localhost:3003',
    'http://localhost:3033',
  ];

  static formToken: string | null = this.getFormTokenFromURLSearchParams();
  static isTemplate: boolean = this.checkIfFormIsTemplate();

  static checkIfLoadedFromCNAME() {
    const domain = document?.location?.host;
    if (domain && !this.staticHostingDomains.includes(domain)) {
      return true;
    }
    return false;
  }

  static async getFormTokenFromCNAMEDomain() {
    const domain = document?.location?.host;
    if (domain && !this.staticHostingDomains.includes(domain)) {
      const formToken = await APIService.formsService.getTokenFromDomain();
      if (formToken) {
        return formToken;
      }
    }

    return null;
  }

  static getFormTokenFromURLSearchParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const formToken = urlParams.get('formToken');
    if (formToken) {
      return formToken;
    }
    return null;
  }

  static reportFirstInteraction() {
    ParentAppService.sendMessage({
      key: createMessageKey(this.formToken),
      command: WidgetAppMessageCommand.FIRST_INTERACTION,
      data: {},
    });
  }

  static async windowEventListener(msg: MessageEvent<any>, dispatch: Dispatch) {
    const messageData: ParentAppMessage | WidgetAppMessage = msg.data;

    if (
      messageData.key &&
      checkIfValidTapformMessageKey(
        messageData.key,
        ParentAppService.formToken,
      ) &&
      messageData.command &&
      messageData.data
    ) {
      switch (messageData.command) {
        case ParentAppMessageCommand.LOAD_FORM: {
          const messageDataParsed = messageData as IncomingLoadFormMessage;
          APIService.apiUrl = messageDataParsed.data.apiUrl;
          APIBaseService.apiUrl = messageDataParsed.data.apiUrl;
          APIService.formsService.apiUrl = messageDataParsed.data.apiUrl;
          APIService.imagesService.apiUrl = messageDataParsed.data.apiUrl;
          APIService.statsService.apiUrl = messageDataParsed.data.apiUrl;
          dispatch(
            actionLoadForm({
              data: messageDataParsed.data.configuration.data,
            }),
          );
          if (messageDataParsed.data.isIframe) {
            dispatch(
              actionUIOpen({
                isFromLink: false,
                isFixed: true,
              }),
            );
            window.addEventListener(
              'click',
              () => {
                ParentAppService.reportFirstInteraction();
              },
              { once: true },
            );
          }
          return;
        }

        case WidgetAppMessageCommand.SEND: {
          if (this.formToken) {
            const sendData = messageData.data as SendRequestMessageData;
            const response = await fetch(sendData.url, {
              ...sendData.options,
              headers: {
                ...sendData.options.headers,
                'tapform-token': this.formToken,
              },
            });
            const body = await response.json();
            const msgData: IncomingResponseMessage = {
              key: createMessageKey(this.formToken),
              command: ParentAppMessageCommand.RESPONSE,
              data: {
                key: sendData.uuid,
                body,
                status: response.status,
              },
            };
            ParentAppService.sendMessage(msgData);
          }
          return true;
          // return dispatch(actionLoadResponse());
        }

        case WidgetAppMessageCommand.INIT: {
          let formToken: string | null = null;
          const formSlug = this.checkIfFormLink();
          if (typeof formSlug !== 'string' && !this.isTemplate) {
            const formTokenFromDomain =
              await this.getFormTokenFromCNAMEDomain();
            if (formTokenFromDomain) {
              formToken = formTokenFromDomain;
            }
          }
          if (this.isTemplate && !formToken) {
            formToken = window.location.pathname.split('/').slice(2)[0];
          }

          if (
            !this.checkIfInsideWebsite() ||
            formSlug === process.env.REACT_APP_FORM_SLUG_FOR_LANDING
          ) {
            try {
              if (!formToken && typeof formSlug === 'string') {
                formToken = await APIService.formsService.getTokenFromSlug(
                  formSlug,
                );
              }
              if (formToken) {
                const configuration = await APIService.formsService.loadClient(
                  formToken,
                  this.isTemplate || undefined,
                );
                if (configuration) {
                  this.formToken = formToken;
                  dispatch(
                    actionLoadForm(
                      {
                        data: configuration.data,
                      },
                      this.isTemplate ? this.getTemplateStepId() : undefined,
                    ),
                  );
                  setTimeout(() => {
                    // document.title = configuration.data.widgetTitle;
                    dispatch(actionUIOpen({ isFromLink: true }));
                    if (!checkForTapformDemoSubmissionParam()) {
                      APIService.statsService.report({
                        type: StatsType.OPEN_FROM_LINK,
                        clientKey: configuration.data.clientKey,
                      });
                    }
                  }, 0);
                }
                return;
              }
            } catch (e) {
              return;
            }
          }
          return;
          // return dispatch(actionLoadResponse());
        }

        case ParentAppMessageCommand.OPEN: {
          const messageDataParsed = messageData as IncomingOpenMessage;
          return dispatch(
            actionUIOpen({
              isFixed: messageDataParsed.data?.isFixed,
              isFromLink: false,
            }),
          );
        }

        case ParentAppMessageCommand.GIVE_CONSENT: {
          return dispatch(actionGiveConsent());
        }

        case ParentAppMessageCommand.REMOVE_CONSENT: {
          return dispatch(actionRemoveConsent());
        }

        case ParentAppMessageCommand.RESPONSE: {
          const messageDataParsed = messageData as IncomingResponseMessage;
          return dispatch(actionLoadResponse(messageDataParsed.data));
        }

        default:
          if (process.env.NODE_ENV === 'development') {
            console.log('Unknown window message', messageData);
          }
      }
    }
  }

  static initListener() {
    const dispatch = useDispatch();

    React.useEffect(() => {
      const ec = (msg: MessageEvent<any>) =>
        this.windowEventListener(msg, dispatch);
      window.addEventListener('message', ec);
      return () => {
        window.removeEventListener('message', ec);
      };
    }, [dispatch]);
  }

  private static sendMessage(data: SendMessagePayload) {
    if (this.checkIfInsideWebsite()) {
      window.parent.postMessage(data, '*');
    } else {
      window.postMessage(data, '*');
    }
  }

  static sendInitMessage(data: SendInitMessageData) {
    ParentAppService.sendMessage({
      key: createMessageKey(this.formToken),
      command: WidgetAppMessageCommand.INIT,
      data,
    });
  }

  static checkIfInsideWebsite() {
    const formSlug = this.checkIfFormLink();
    if (
      ParentAppService.dashboardDomains.find((dd) =>
        document?.referrer?.includes(dd),
      )
    ) {
      return false; // This is onboarding
    }
    if (
      typeof formSlug === 'string' &&
      formSlug === process.env.REACT_APP_FORM_SLUG_FOR_LANDING
    ) {
      return false;
    }
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  static checkIfFormLink(): string | boolean {
    const fromCNAME = this.checkIfLoadedFromCNAME();
    if (fromCNAME) {
      return true;
    }
    if (location && location.pathname !== '/') {
      const pathSplit = location.pathname.split('/').slice(1);
      if (pathSplit.length === 1 || pathSplit.length === 2) {
        return pathSplit[0];
      }
    }
    return false;
  }

  static checkIfFormIsTemplate(): boolean {
    const isFromLink = this.checkIfFormLink();
    const paths = window.location.pathname.split('/').slice(1);
    if (
      isFromLink &&
      typeof isFromLink === 'string' &&
      paths.length === 2 &&
      paths[0] === 'template'
    ) {
      return true;
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      const isTemplate = urlParams.get('isTemplate');
      return isTemplate === 'true';
    }
  }

  static getTemplateStepId(): number | undefined {
    if (!this.isTemplate) {
      return undefined;
    }
    const search = window?.location?.search;
    if (search?.length) {
      const searchParams = new URLSearchParams(search);
      const templateStepId = searchParams.get('templateStep');
      return parseInt(templateStepId as string, 10);
    }
    return undefined;
  }

  static sendExitedMessage(data: SendExitedMessageData) {
    ParentAppService.sendMessage({
      key: createMessageKey(this.formToken),
      command: WidgetAppMessageCommand.EXITED,
      data,
    });
  }

  static sendCloseMessage(data: SendCloseMessageData) {
    ParentAppService.sendMessage({
      key: createMessageKey(this.formToken),
      command: WidgetAppMessageCommand.CLOSE,
      data,
    });
  }

  static sendRequestMessage(data: SendRequestMessageData) {
    ParentAppService.sendMessage({
      key: createMessageKey(this.formToken),
      command: WidgetAppMessageCommand.SEND,
      data,
    });
  }

  static generateUUID() {
    if (window.crypto.randomUUID as any) {
      return window.crypto.randomUUID();
    }
    return uuidv4().toString();
  }
}

export default ParentAppService;
